import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import store from 'store'
import useI18n from '../../i18n/useI18n'
import Modal from '../../components/modal'
import Input from '../../components/input'
import Select from '../../components/select'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { 
  ApplicationStatusEnum, 
  ApplicationStatusOptions, 
  ApplicationPeriodEnum, 
  ApplicationPeriodOptions,
  DegreeOptions,
  EducationLanguageOptions,
  UserTypeEnum
} from '../../utils/enums'
import general from '../../utils/general'
import EventBus from '../../infrastructure/event-bus/event-bus'
import { getMissingStudentFields } from '../../utils/studentValidation'

const ApplicationEditModal = ({ isOpen, application, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const userInfo = useSelector((state) => state.auth?.userInfo || {})
  const { t } = useI18n()
  const [formData, setFormData] = useState({
    programId: '',
    universityId: '',
    programType: '',
    educationLanguage: '',
    applicationStatus: '',
    applicationDate: '',
    submissionDate: '',
    reviewStartDate: '',
    reviewEndDate: '',
    decisionDate: '',
    decisionNotes: '',
    applicationYear: '',
    applicationPeriod: ''
  })
  const [validationErrors, setValidationErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)
  const [programDetails, setProgramDetails] = useState(null)
  const [loadingProgram, setLoadingProgram] = useState(false)
  const [programs, setPrograms] = useState([])
  const [loadingPrograms, setLoadingPrograms] = useState(false)
  const [selectedUniversityOption, setSelectedUniversityOption] = useState(null)

  useEffect(() => {
    if (isOpen && application?.id) {
      if (application.programId || application.applicationStatus) {
        // Form data already available
        mapApplicationToForm(application)
      } else {
        // Fetch full details
        fetchApplicationDetails()
      }
    }

    if (!isOpen) {
      setFormData({
        programId: '',
        universityId: '',
        programType: '',
        educationLanguage: '',
        applicationStatus: '',
        applicationDate: '',
        submissionDate: '',
        reviewStartDate: '',
        reviewEndDate: '',
        decisionDate: '',
        decisionNotes: '',
        applicationYear: '',
        applicationPeriod: ''
      })
      setValidationErrors({})
      setProgramDetails(null)
      setSelectedUniversityOption(null)
    }
  }, [isOpen, application?.id])

  const fetchApplicationDetails = () => {
    setLoading(true)
    dispatch(
      ApiCallActions.Get({
        url: `applications/${application.id}`,
        showAlertOnError: true,
        showLoading: false,
        onSuccess: ({ data }) => {
          const payload = data?.data || data || null
          if (payload) {
            mapApplicationToForm(payload)
          }
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          if (errorMessage) {
            general.notificationError(errorMessage)
          }
          setLoading(false)
        }
      })
    )
  }

  const mapApplicationToForm = (appData) => {
    const formatDateForInput = (dateString) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toISOString().split('T')[0]
      } catch {
        return dateString
      }
    }

    const formatDateTimeForInput = (dateString) => {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        return localDateTime.toISOString().slice(0, 16)
      } catch {
        return dateString
      }
    }

    const programId = appData.programId?.toString() || appData.program?.id?.toString() || ''
    const program = appData.program || {}
    const resolvedUniversity =
      appData.university ||
      program.university ||
      null
    const resolvedUniversityId =
      program.universityId?.toString() ||
      program.university?.id?.toString() ||
      appData.universityId?.toString() ||
      appData.university?.id?.toString() ||
      ''
    
    setFormData({
      programId: programId,
      universityId: resolvedUniversityId,
      programType: program.degree || '',
      educationLanguage: program.language || '',
      applicationStatus: appData.applicationStatus || appData.status || '',
      applicationDate: formatDateForInput(appData.applicationDate),
      submissionDate: formatDateTimeForInput(appData.submissionDate),
      reviewStartDate: formatDateForInput(appData.reviewStartDate),
      reviewEndDate: formatDateForInput(appData.reviewEndDate),
      decisionDate: formatDateForInput(appData.decisionDate),
      decisionNotes: appData.decisionNotes || '',
      applicationYear: appData.applicationYear?.toString() || '',
      applicationPeriod: appData.applicationPeriod || ''
    })

    // Fetch program details if programId exists
    if (programId) {
      fetchProgramDetails(programId)
    } else if (appData.program) {
      // If program object is available, use it directly
      setProgramDetails(appData.program)
    }
    
    // Fetch programs for the selected university
    if (resolvedUniversity?.id && resolvedUniversity?.name) {
      setSelectedUniversityOption({
        value: resolvedUniversity.id.toString(),
        label: resolvedUniversity.name
      })
    } else {
      setSelectedUniversityOption(null)
    }

    const universityId = resolvedUniversityId
    if (universityId) {
      fetchProgramsByUniversity(universityId, '', '')
    }
  }

  const handleChange = (name, value) => {
    setFormData(prev => {
      const newFormData = { ...prev, [name]: value }
      
      // If university changed, reset program, programType, and educationLanguage
      if (name === 'universityId') {
        newFormData.programId = ''
        newFormData.programType = ''
        newFormData.educationLanguage = ''
        setProgramDetails(null)
        // Fetch programs for the selected university
        if (value) {
          fetchProgramsByUniversity(value, '', '')
        } else {
          setPrograms([])
        }
      }
      
      // If programType or educationLanguage changed, filter programs and reset programId
      if (name === 'programType' || name === 'educationLanguage') {
        newFormData.programId = ''
        setProgramDetails(null)
        // Refetch programs with new filters
        if (prev.universityId) {
          const programType = name === 'programType' ? value : prev.programType
          const educationLanguage = name === 'educationLanguage' ? value : prev.educationLanguage
          fetchProgramsByUniversity(prev.universityId, programType, educationLanguage)
        }
      }
      
      return newFormData
    })
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    // If program changed, fetch program details and update related fields
    if (name === 'programId' && value) {
      fetchProgramDetails(value)
    }
  }

  const fetchProgramsByUniversity = (universityId, programType = '', educationLanguage = '') => {
    if (!universityId) {
      setPrograms([])
      return
    }

    setLoadingPrograms(true)
    const queryParams = { university_id: universityId, limit: 99 }
    if (programType) queryParams.degree = programType
    if (educationLanguage) queryParams.language = educationLanguage

    dispatch(
      ApiCallActions.Get({
        url: 'programs',
        query: queryParams,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
          setPrograms(list)
          setLoadingPrograms(false)
        },
        onError: () => {
          setPrograms([])
          setLoadingPrograms(false)
        }
      })
    )
  }

  const fetchProgramDetails = (programId) => {
    if (!programId) {
      setProgramDetails(null)
      setFormData(prev => ({
        ...prev,
        programType: '',
        educationLanguage: ''
      }))
      return
    }

    setLoadingProgram(true)
    dispatch(
      ApiCallActions.Get({
        url: `programs/${programId}`,
        showAlertOnError: false,
        showLoading: false,
        onSuccess: ({ data }) => {
          const program = data?.data || data
          setProgramDetails(program)
          
          // Update form data with program details
          setFormData(prev => ({
            ...prev,
            programType: program.degree || '',
            educationLanguage: program.language || ''
          }))
          
          setLoadingProgram(false)
        },
        onError: () => {
          setProgramDetails(null)
          setFormData(prev => ({
            ...prev,
            programType: '',
            educationLanguage: ''
          }))
          setLoadingProgram(false)
        }
      })
    )
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.universityId || formData.universityId.trim() === '') {
      errors.universityId = t('APPLICATIONS_FORM_VALIDATION_UNIVERSITY_REQUIRED')
    }

    if (formData.universityId) {
      if (!formData.programType || formData.programType.trim() === '') {
        errors.programType = t('APPLICATIONS_FORM_VALIDATION_PROGRAM_TYPE_REQUIRED')
      }

      if (!formData.educationLanguage || formData.educationLanguage.trim() === '') {
        errors.educationLanguage = t('APPLICATIONS_FORM_VALIDATION_EDUCATION_LANGUAGE_REQUIRED')
      }
    }

    if (!formData.programId || formData.programId.trim() === '') {
      errors.programId = t('APPLICATIONS_FORM_VALIDATION_PROGRAM_REQUIRED')
    }

    if (!formData.applicationStatus || formData.applicationStatus.trim() === '') {
      errors.applicationStatus = t('APPLICATIONS_FORM_VALIDATION_STATUS_REQUIRED')
    }

    if (!formData.applicationYear || formData.applicationYear.trim() === '') {
      errors.applicationYear = t('APPLICATIONS_FORM_VALIDATION_YEAR_REQUIRED')
    }

    if (!formData.applicationPeriod || formData.applicationPeriod.trim() === '') {
      errors.applicationPeriod = t('APPLICATIONS_FORM_VALIDATION_PERIOD_REQUIRED')
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const buildSubmitData = () => {
    const submitData = {
      programId: parseInt(formData.programId, 10),
      applicationStatus: formData.applicationStatus,
      applicationYear: parseInt(formData.applicationYear, 10)
    }

    if (formData.applicationDate) submitData.applicationDate = formData.applicationDate
    if (formData.submissionDate) submitData.submissionDate = formData.submissionDate
    if (formData.reviewStartDate) submitData.reviewStartDate = formData.reviewStartDate
    if (formData.reviewEndDate) submitData.reviewEndDate = formData.reviewEndDate
    if (formData.decisionDate) submitData.decisionDate = formData.decisionDate
    if (formData.decisionNotes) submitData.decisionNotes = formData.decisionNotes.trim()
    if (formData.applicationPeriod) submitData.applicationPeriod = formData.applicationPeriod

    return submitData
  }

  const resolveStudentData = () => {
    const fromUser = userInfo?.student || userInfo?.studentInfo || userInfo?.studentProfile
    return fromUser || store.get('studentProfile') || null
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (userInfo?.userType === UserTypeEnum.STUDENT) {
      const studentData = resolveStudentData()
      const missingFields = getMissingStudentFields(studentData || {})
      if (missingFields.length > 0) {
        general.notificationError(`Profil bilgilerin eksik: ${missingFields.join(', ')}`)
        return
      }
    }
    
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    const payload = buildSubmitData()

    dispatch(
      ApiCallActions.Put({
        url: `applications/${application.id}`,
        data: payload,
        showAlertOnError: true,
        showLoading: true,
        onSuccess: ({ data }) => {
          const updatedData = data?.data || data
          
          EventBus.sc.onEntityUpdated({
            entityType: 'application',
            entityId: application.id,
            entityData: updatedData
          })

          general.messageSuccess(t('APPLICATIONS_FORM_UPDATE_SUCCESS'))
          setIsSubmitting(false)
          
          if (onSuccess) {
            onSuccess(updatedData)
          }
          
          onClose()
        },
        onError: ({ errorMessage }) => {
          general.notificationError(errorMessage || t('APPLICATIONS_FORM_UPDATE_ERROR'))
          setIsSubmitting(false)
        }
      })
    )
  }

  // Generate year options (current year and next 5 years)
  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 6 }, (_, i) => {
    const year = currentYear + i
    return { value: year.toString(), label: year.toString() }
  })

  const statusOptions = ApplicationStatusOptions.map(opt => ({
    value: opt.value,
    label: t(opt.label)
  }))

  const periodOptions = ApplicationPeriodOptions.map(opt => ({
    value: opt.value,
    label: t(opt.label)
  }))

  // Get unique program types and education languages from programs list
  const availableProgramTypes = useMemo(() => {
    if (!programs.length) return []
    const uniqueDegrees = [...new Set(programs.map(p => p.degree).filter(Boolean))]
    return uniqueDegrees.map(degree => {
      const option = DegreeOptions.find(opt => opt.value === degree)
      return option || { value: degree, label: degree }
    })
  }, [programs])

  const availableEducationLanguages = useMemo(() => {
    if (!programs.length) return []
    const uniqueLanguages = [...new Set(programs.map(p => p.language).filter(Boolean))]
    return uniqueLanguages.map(language => {
      const option = EducationLanguageOptions.find(opt => opt.value === language)
      return option || { value: language, label: language }
    })
  }, [programs])

  if (!application) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('APPLICATIONS_FORM_TITLE_EDIT')}
      subtitle={t('APPLICATIONS_FORM_SUBTITLE_EDIT')}
      icon="zmdi-edit"
      size="large"
    >
      {loading ? (
        <div className="text-center p-4">
          <p>{t('APPLICATIONS_DETAIL_LOADING')}</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {/* Program Bilgileri Bölümü */}
          <div className="card m-b-20">
            <div className="header">
              <h2>
                <i className="zmdi zmdi-graduation-cap m-r-5"></i>
                {t('APPLICATIONS_FORM_SECTION_PROGRAM')}
              </h2>
            </div>
            <div className="body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_UNIVERSITY')}</label>
                    <Select
                      name="universityId"
                      value={formData.universityId}
                      onChange={(e) => handleChange('universityId', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_UNIVERSITY')}
                      options={[]}
                      fetchUrl="universities"
                      valueKey="id"
                      labelKey="name"
                      prependOptions={selectedUniversityOption ? [selectedUniversityOption] : []}
                      required
                      icon="zmdi-city"
                    />
                    {validationErrors.universityId && (
                      <small className="text-danger">{validationErrors.universityId}</small>
                    )}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_PROGRAM')}</label>
                    <Select
                      name="programId"
                      value={formData.programId}
                      onChange={(e) => handleChange('programId', e.target.value)}
                      placeholder={loadingPrograms ? t('APPLICATIONS_FORM_LOADING_PROGRAM') : t('APPLICATIONS_FORM_FIELD_PROGRAM')}
                      options={programs.map(program => ({
                        value: program.id?.toString() || '',
                        label: program.name || program.nameEn || ''
                      }))}
                      required
                      errorMessage={validationErrors.programId}
                      icon="zmdi-graduation-cap"
                      disabled={!formData.universityId || loadingPrograms}
                    />
                    {validationErrors.programId && (
                      <small className="text-danger">{validationErrors.programId}</small>
                    )}
                    {!formData.universityId && (
                      <small className="text-muted">{t('APPLICATIONS_FORM_SELECT_UNIVERSITY_FIRST')}</small>
                    )}
                  </div>
                </div>
              </div>

              {formData.universityId && (
                <>
                  <div className="row m-t-15">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>{t('APPLICATIONS_FORM_FIELD_PROGRAM_TYPE')}</label>
                        <Select
                          name="programType"
                          value={formData.programType}
                          onChange={(e) => handleChange('programType', e.target.value)}
                          placeholder={loadingPrograms ? t('APPLICATIONS_FORM_LOADING_PROGRAM') : t('APPLICATIONS_FORM_FIELD_PROGRAM_TYPE')}
                          options={availableProgramTypes.map(opt => ({
                            value: opt.value,
                            label: t(opt.label || opt.value)
                          }))}
                          required
                          icon="zmdi-graduation-cap"
                          disabled={loadingPrograms || !formData.universityId}
                        />
                        {validationErrors.programType && (
                          <small className="text-danger">{validationErrors.programType}</small>
                        )}
                        {!formData.universityId && (
                          <small className="text-muted">{t('APPLICATIONS_FORM_SELECT_UNIVERSITY_FIRST')}</small>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>{t('APPLICATIONS_FORM_FIELD_EDUCATION_LANGUAGE')}</label>
                        <Select
                          name="educationLanguage"
                          value={formData.educationLanguage}
                          onChange={(e) => handleChange('educationLanguage', e.target.value)}
                          placeholder={loadingPrograms ? t('APPLICATIONS_FORM_LOADING_PROGRAM') : t('APPLICATIONS_FORM_FIELD_EDUCATION_LANGUAGE')}
                          options={availableEducationLanguages.map(opt => ({
                            value: opt.value,
                            label: t(opt.label || opt.value)
                          }))}
                          required
                          icon="zmdi-translate"
                          disabled={loadingPrograms || !formData.universityId}
                        />
                        {validationErrors.educationLanguage && (
                          <small className="text-danger">{validationErrors.educationLanguage}</small>
                        )}
                        {!formData.universityId && (
                          <small className="text-muted">{t('APPLICATIONS_FORM_SELECT_UNIVERSITY_FIRST')}</small>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}

              {loadingProgram && (
                <div className="text-center p-3">
                  <small className="text-muted">{t('APPLICATIONS_FORM_LOADING_PROGRAM')}</small>
                </div>
              )}
            </div>
          </div>

          {/* Başvuru Bilgileri Bölümü */}
          <div className="card m-b-20">
            <div className="header">
              <h2>
                <i className="zmdi zmdi-file-text m-r-5"></i>
                {t('APPLICATIONS_FORM_SECTION_APPLICATION')}
              </h2>
            </div>
            <div className="body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_STATUS')}</label>
                    <Select
                      name="applicationStatus"
                      value={formData.applicationStatus}
                      onChange={(e) => handleChange('applicationStatus', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_STATUS')}
                      options={statusOptions}
                      required
                      icon="zmdi-info"
                    />
                    {validationErrors.applicationStatus && (
                      <small className="text-danger">{validationErrors.applicationStatus}</small>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_APPLICATION_YEAR')}</label>
                    <Select
                      name="applicationYear"
                      value={formData.applicationYear}
                      onChange={(e) => handleChange('applicationYear', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_APPLICATION_YEAR')}
                      options={yearOptions}
                      required
                      icon="zmdi-calendar-note"
                    />
                    {validationErrors.applicationYear && (
                      <small className="text-danger">{validationErrors.applicationYear}</small>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_APPLICATION_PERIOD')}</label>
                    <Select
                      name="applicationPeriod"
                      value={formData.applicationPeriod}
                      onChange={(e) => handleChange('applicationPeriod', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_APPLICATION_PERIOD')}
                      options={periodOptions}
                      required
                      icon="zmdi-calendar-note"
                    />
                    {validationErrors.applicationPeriod && (
                      <small className="text-danger">{validationErrors.applicationPeriod}</small>
                    )}
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_APPLICATION_DATE')}</label>
                    <Input
                      type="date"
                      name="applicationDate"
                      value={formData.applicationDate}
                      onChange={(e) => handleChange('applicationDate', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_APPLICATION_DATE')}
                      icon="zmdi-calendar"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_SUBMISSION_DATE')}</label>
                    <Input
                      type="datetime-local"
                      name="submissionDate"
                      value={formData.submissionDate}
                      onChange={(e) => handleChange('submissionDate', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_SUBMISSION_DATE')}
                      icon="zmdi-time"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* İnceleme Bilgileri Bölümü */}
          <div className="card m-b-20">
            <div className="header">
              <h2>
                <i className="zmdi zmdi-check-circle m-r-5"></i>
                {t('APPLICATIONS_FORM_SECTION_REVIEW')}
              </h2>
            </div>
            <div className="body">
              <div className="row">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_REVIEW_START_DATE')}</label>
                    <Input
                      type="date"
                      name="reviewStartDate"
                      value={formData.reviewStartDate}
                      onChange={(e) => handleChange('reviewStartDate', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_REVIEW_START_DATE')}
                      icon="zmdi-calendar-check"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_REVIEW_END_DATE')}</label>
                    <Input
                      type="date"
                      name="reviewEndDate"
                      value={formData.reviewEndDate}
                      onChange={(e) => handleChange('reviewEndDate', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_REVIEW_END_DATE')}
                      icon="zmdi-calendar-check"
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_DECISION_DATE')}</label>
                    <Input
                      type="date"
                      name="decisionDate"
                      value={formData.decisionDate}
                      onChange={(e) => handleChange('decisionDate', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_DECISION_DATE')}
                      icon="zmdi-check-circle"
                    />
                  </div>
                </div>
              </div>

              <div className="row m-t-15">
                <div className="col-12">
                  <div className="form-group">
                    <label>{t('APPLICATIONS_FORM_FIELD_DECISION_NOTES')}</label>
                    <textarea
                      className="form-control"
                      name="decisionNotes"
                      value={formData.decisionNotes}
                      onChange={(e) => handleChange('decisionNotes', e.target.value)}
                      placeholder={t('APPLICATIONS_FORM_FIELD_DECISION_NOTES_PLACEHOLDER')}
                      rows="4"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row m-t-20">
            <div className="col-12">
              <div className="d-flex justify-content-end" style={{ gap: '10px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  {t('APPLICATIONS_FORM_BUTTON_CANCEL')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('APPLICATIONS_FORM_BUTTON_UPDATING') : t('APPLICATIONS_FORM_BUTTON_UPDATE')}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}

export default ApplicationEditModal

